// @ts-nocheck
import _ from 'lodash';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useResizeObserver from 'use-resize-observer/polyfilled';
import { makeStyles, Box } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useIfMounted } from '../hooks';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    width: '100%',
    height: '100%',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    padding: theme.spacing(1),
  },
  fill: {
    flex: '1',
  },
  table: {
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderColor: theme.palette.divider,
    },
    '& .MuiDataGrid-columnSeparator': {
      color: theme.palette.divider,
    },
    '& .MuiDataGrid-columnsContainer': {
      borderColor: theme.palette.divider,
    },
    '& .MuiDataGrid-toolbarContainer': {
      justifyContent: 'flex-end',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: '0',
      '& > .MuiFormControl-root': {
        minWidth: '200px',
        margin: theme.spacing(1),
      },
      '& > .MuiButton-root': {
        margin: theme.spacing(1),
      },
    },
  },
  disabledFooter: {
    '& .MuiDataGrid-footerContainer': {
      pointerEvents: 'none',
    },
  },
}));

function DataTable(props) {
  const isJSDOM = /jsdom/.test(window.navigator.userAgent);

  const [showDataGrid, setShowDataGrid] = useState(isJSDOM);
  const [disableFooter, setDisableFooter] = useState(false);
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const onResize = useMemo(
    () =>
      _.debounce(size => {
        const show = size.width > 0 && size.height > 0;
        ifMounted(() => setShowDataGrid(show));
      }, 0),
    [ifMounted]
  );
  const { ref } = useResizeObserver({ onResize });

  const {
    id,
    className,
    components,
    columns,
    rows,
    rowCount,
    pageSize,
    pageIndex,
    onPageChange,
    onRowClick,
    onRowSelected,
    onSelectionModelChange,
    onSortModelChange,
    sortingMode,
    sortingOrder,
    sortModel,
    disableRowClick,
    checkboxSelection,
    selectionModel,
    paginationMode,
    pagination,
    isRowSelectable,
    autoHeight,
  } = props;

  const handleRowClick = async params => {
    if (!onRowClick || disableRowClick) {
      return;
    }
    await onRowClick(params);
  };

  const handlePageChange = async params => {
    if (!onPageChange) {
      return;
    }
    setDisableFooter(true);
    await onPageChange(params.page + 1);
    ifMounted(() => setDisableFooter(false));
  };

  return (
    <Box className={classes.container} ref={ref}>
      {showDataGrid && (
        <DataGrid
          id={id}
          components={components}
          className={clsx(classes.table, className, {
            [classes.disabledFooter]: disableFooter,
          })}
          columnBuffer={5}
          autoHeight={autoHeight}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          pageSize={pageSize}
          page={pageIndex}
          onPageChange={handlePageChange}
          onRowClick={handleRowClick}
          onRowSelected={onRowSelected}
          onSelectionModelChange={onSelectionModelChange}
          onSortModelChange={onSortModelChange}
          sortingMode={sortingMode}
          sortingOrder={sortingOrder}
          sortModel={sortModel}
          checkboxSelection={checkboxSelection}
          selectionModel={selectionModel}
          paginationMode={paginationMode}
          pagination={pagination}
          isRowSelectable={isRowSelectable}
        />
      )}
    </Box>
  );
}

DataTable.defaultProps = {
  id: undefined,
  className: undefined,
  components: undefined,
  rowCount: undefined,
  pageSize: undefined,
  pageIndex: undefined,
  onPageChange: undefined,
  onRowClick: undefined,
  onRowSelected: undefined,
  onSelectionModelChange: undefined,
  onSortModelChange: undefined,
  sortingMode: undefined,
  sortingOrder: undefined,
  sortModel: undefined,
  disableRowClick: undefined,
  checkboxSelection: undefined,
  selectionModel: undefined,
  paginationMode: undefined,
  pagination: undefined,
  isRowSelectable: undefined,
  autoHeight: false,
};

DataTable.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  components: PropTypes.shape({
    Toolbar: PropTypes.elementType,
    LoadingOverlay: PropTypes.elementType,
    NoRowsOverlay: PropTypes.elementType,
    Pagination: PropTypes.elementType,
  }),
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowCount: PropTypes.number,
  pageSize: PropTypes.number,
  pageIndex: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowClick: PropTypes.func,
  onRowSelected: PropTypes.func,
  onSelectionModelChange: PropTypes.func,
  onSortModelChange: PropTypes.func,
  sortingMode: PropTypes.oneOf(['client', 'server']),
  sortingOrder: PropTypes.arrayOf(PropTypes.string),
  sortModel: PropTypes.arrayOf(PropTypes.any),
  disableRowClick: PropTypes.bool,
  checkboxSelection: PropTypes.bool,
  selectionModel: PropTypes.arrayOf(PropTypes.any),
  paginationMode: PropTypes.oneOf(['client', 'server']),
  pagination: PropTypes.bool,
  isRowSelectable: PropTypes.func,
  autoHeight: PropTypes.bool,
};

export default DataTable;
